// Solposisjon og lysfaser for operasjonsplanlegging: lysdisiplin, NVG-vindu og
// BMNT/EENT slik begrepene brukes i ordreverket. Primærområdet er Oslofjorden
// (~59-60°N), men koden er skrevet for å være korrekt helt til Nord-Norge, der
// midnattssol og mørketid gjør at «soloppgang»/«solnedgang» kan mangle helt.
//
// ── ALGORITME OG NØYAKTIGHET ────────────────────────────────────────────────
// Kilde: J. Meeus, «Astronomical Algorithms», 2. utg. (1998), kap. 12 (siderisk
// tid), 13 (koordinat-transformasjon), 16.4 (refraksjon), 25 (solkoordinater,
// lav-presisjons-varianten) og 28 (tidsligningen). Dette er samme matematikk
// som NOAA Solar Calculator og NREL SPA-lite bygger på.
//
//   * Solas geosentriske posisjon: ~0.01° (36") feil i ekliptisk lengde, i
//     praksis < 1' i asimut/elevasjon. Ingen parallakse-korreksjon (solas
//     horisontalparallakse er 8.8" ≈ 0.0024°, forsømbar).
//   * Refraksjon: Sæmundsson (Meeus 16.4) for standardatmosfære (1010 mbar,
//     10 °C). MERK: nær horisonten er den REELLE refraksjonen sterkt
//     temperatur- og trykkavhengig (34' ± 10', mer ved inversjon over kald
//     fjord). Oppgang/nedgang har derfor en FYSISK usikkerhet på flere
//     minutter uansett hvor god matematikken er. Ikke planlegg med
//     sekund-presisjon på horisont-fenomener.
//   * Opp-/nedgang og tussmørke finnes ved numerisk rot-søk (grovskann +
//     bisekssjon) på høydefunksjonen — ikke med lukket formel. Det gir riktig
//     oppførsel på høye breddegrader der sola skjærer terskelen nesten
//     tangentielt, og gjør at «forekommer ikke» kan skilles fra «alltid over»
//     (midnattssol) og «alltid under» (mørketid).
//   * Ingen korreksjon for observatørens høyde over havet (en observatør 300 m
//     oppe ser sola gå ned ~2-3 min senere enn en ved havflaten) og ingen
//     terrenghorisont. Terrengets skyggevirkning ligger i `shadow.ts`.
//
// Modulen overlapper bevisst med `@skade/contracts` `astro.ts`, som har en
// enklere SunCalc-basert `sunTimes()`. Denne modulen er den presise varianten
// og legger til: solposisjon (asimut/elevasjon), astronomisk tussmørke (−18°),
// eksplisitt tilstand ved midnattssol/mørketid, og tid til neste fase-overgang.
// `astro.ts` er beholdt urørt; testene her krysssjekker mot den.
const DEG = Math.PI / 180;
const RAD = 180 / Math.PI;
const DAY_MS = 86_400_000;
/** Julian Day for Unix-epoken 1970-01-01T00:00:00Z. */
const JD_UNIX_EPOCH = 2440587.5;
/**
 * ΔT = TT − UT (sekunder). Målt verdi har ligget rundt 69 s gjennom hele
 * 2020-tallet (IERS Bulletin A). Vi bruker en konstant: en feil på ±10 s
 * flytter sola 0.0001° i lengde og er uten praktisk betydning her.
 */
const DELTA_T_S = 69;
/** Julian Day (UT) fra Unix-millisekund. */
export function julianDay(timeMs) {
    return timeMs / DAY_MS + JD_UNIX_EPOCH;
}
/** Julian Ephemeris Day (TT = UT + ΔT) fra Unix-millisekund. */
export function julianEphemerisDay(timeMs) {
    return julianDay(timeMs) + DELTA_T_S / 86_400;
}
/** Normaliserer til [0, 360). */
export function normalizeDeg(x) {
    const r = x % 360;
    return r < 0 ? r + 360 : r;
}
/** Normaliserer til [−180, 180). */
export function normalizeDeg180(x) {
    const r = normalizeDeg(x);
    return r >= 180 ? r - 360 : r;
}
/**
 * Solas tilsynelatende geosentriske ekvatorialkoordinater (Meeus kap. 25,
 * «lower accuracy» + apparent-korreksjonene i 25.10-25.11, tidsligningen 28.3).
 * `jde` er Julian Ephemeris Day (TT). Eksponert fordi den verifiseres direkte
 * mot Meeus' eksempel 25.b i testene.
 */
export function sunApparentEquatorial(jde) {
    const T = (jde - 2451545) / 36525;
    // Geometrisk middellengde og middelanomali.
    const L0 = normalizeDeg(280.46646 + 36000.76983 * T + 0.0003032 * T * T);
    const M = normalizeDeg(357.52911 + 35999.05029 * T - 0.0001537 * T * T);
    const ecc = 0.016708634 - 0.000042037 * T - 0.0000001267 * T * T;
    const Mr = M * DEG;
    // Midtpunktsligningen (equation of the center).
    const C = (1.914602 - 0.004817 * T - 0.000014 * T * T) * Math.sin(Mr) +
        (0.019993 - 0.000101 * T) * Math.sin(2 * Mr) +
        0.000289 * Math.sin(3 * Mr);
    const trueLongitude = L0 + C;
    const trueAnomaly = (M + C) * DEG;
    // Radiusvektor (AU), Meeus 25.5.
    const distanceAu = (1.000001018 * (1 - ecc * ecc)) / (1 + ecc * Math.cos(trueAnomaly));
    // Månebanens stigende knute — driver både nutasjon og aberrasjon-tilnærmingen.
    const omega = (125.04 - 1934.136 * T) * DEG;
    // Tilsynelatende lengde: −0.00569° aberrasjon, −0.00478·sin Ω° nutasjon.
    const apparentLongitudeDeg = trueLongitude - 0.00569 - 0.00478 * Math.sin(omega);
    const nutationLongitudeDeg = -0.00478 * Math.sin(omega);
    // Midlere obliquitet (Meeus 22.2) + nutasjon i obliquitet (tilnærmet).
    const eps0 = 23.439291111111 - 0.013004166667 * T - 1.638889e-7 * T * T + 5.036111e-7 * T * T * T;
    const trueObliquityDeg = eps0 + 0.00256 * Math.cos(omega);
    const lam = apparentLongitudeDeg * DEG;
    const eps = trueObliquityDeg * DEG;
    const rightAscensionDeg = normalizeDeg(Math.atan2(Math.cos(eps) * Math.sin(lam), Math.cos(lam)) * RAD);
    const declinationDeg = Math.asin(Math.sin(eps) * Math.sin(lam)) * RAD;
    // Tidsligningen (Meeus 28.3): E = L0 − 0.0057183° − α + Δψ·cos ε.
    const eDeg = L0 - 0.0057183 - rightAscensionDeg + nutationLongitudeDeg * Math.cos(eps);
    const equationOfTimeMin = normalizeDeg180(eDeg) * 4;
    return {
        rightAscensionDeg,
        declinationDeg,
        apparentLongitudeDeg: normalizeDeg(apparentLongitudeDeg),
        trueObliquityDeg,
        nutationLongitudeDeg,
        distanceAu,
        equationOfTimeMin,
    };
}
/**
 * Tilsynelatende Greenwich siderisk tid (grader). Meeus 12.4 med nutasjons-
 * korreksjon. `jd` skal være Julian Day i UT (ikke TT) — siderisk tid følger
 * jordrotasjonen.
 */
export function apparentSiderealTimeDeg(jd, nutationLongitudeDeg, obliquityDeg) {
    const T = (jd - 2451545) / 36525;
    const theta = 280.46061837 +
        360.98564736629 * (jd - 2451545) +
        0.000387933 * T * T -
        (T * T * T) / 38_710_000;
    return normalizeDeg(theta + nutationLongitudeDeg * Math.cos(obliquityDeg * DEG));
}
/**
 * Ekvatorial → horisontal (Meeus 13.5-13.6), med asimut regnet fra NORD med
 * klokka (navigasjons-konvensjon, ikke Meeus' vestover-fra-sør).
 */
export function equatorialToHorizontal(hourAngleDeg, declinationDeg, latDeg) {
    const H = hourAngleDeg * DEG;
    const dec = declinationDeg * DEG;
    const phi = latDeg * DEG;
    const sinAlt = Math.sin(phi) * Math.sin(dec) + Math.cos(phi) * Math.cos(dec) * Math.cos(H);
    const elevationDeg = Math.asin(Math.max(-1, Math.min(1, sinAlt))) * RAD;
    const y = -Math.sin(H) * Math.cos(dec);
    const x = Math.sin(dec) * Math.cos(phi) - Math.sin(phi) * Math.cos(dec) * Math.cos(H);
    return { azimuthDeg: normalizeDeg(Math.atan2(y, x) * RAD), elevationDeg };
}
/**
 * Atmosfærisk refraksjon i grader (Sæmundsson, Meeus 16.4), standardatmosfære
 * 1010 mbar / 10 °C. Tar GEOMETRISK (sann) høyde inn. Under ca. −1° geometrisk
 * høyde er «tilsynelatende høyde» meningsløst, så input klemmes der.
 *
 * Realisme: ved horisonten er refraksjonen 34' ± 10' avhengig av
 * temperaturgradient. Kald sjø + varm luft (vanlig i Oslofjorden vinterstid)
 * gir betydelig mer. Dette er den dominerende feilkilden i opp-/nedgangstider.
 */
export function refractionDeg(geometricElevationDeg) {
    const h = Math.max(geometricElevationDeg, -1);
    const arcmin = 1.02 / Math.tan((h + 10.3 / (h + 5.11)) * DEG);
    return arcmin / 60;
}
/**
 * Solposisjon for et tidspunkt (UTC-millisekund) og en posisjon.
 * `lon` er østlig positiv (Oslo ≈ +10.75).
 */
export function sunPosition(timeMs, lat, lon) {
    const eq = sunApparentEquatorial(julianEphemerisDay(timeMs));
    const gst = apparentSiderealTimeDeg(julianDay(timeMs), eq.nutationLongitudeDeg, eq.trueObliquityDeg);
    const hourAngleDeg = normalizeDeg180(gst + lon - eq.rightAscensionDeg);
    const hz = equatorialToHorizontal(hourAngleDeg, eq.declinationDeg, lat);
    return {
        azimuthDeg: hz.azimuthDeg,
        elevationDeg: hz.elevationDeg,
        apparentElevationDeg: hz.elevationDeg + refractionDeg(hz.elevationDeg),
        declinationDeg: eq.declinationDeg,
        rightAscensionDeg: eq.rightAscensionDeg,
        hourAngleDeg,
        equationOfTimeMin: eq.equationOfTimeMin,
        distanceAu: eq.distanceAu,
    };
}
/** Kortform: bare geometrisk solelevasjon (grader). Brukes i rot-søkene. */
export function sunElevationDeg(timeMs, lat, lon) {
    return sunPosition(timeMs, lat, lon).elevationDeg;
}
// ── Lysfaser ────────────────────────────────────────────────────────────────
/**
 * Solhøyde-terskler (geometrisk elevasjon av solas SENTRUM, grader).
 *
 *  −0.833° = soloppgang/-nedgang. Summen av standard refraksjon (34') og solas
 *            halve diameter (16'), dvs. øvre solrand tangerer horisonten.
 *  −6°     = borgerlig tussmørke (civil): kan lese kart uten lys, tydelig
 *            horisont over sjø.
 *  −12°    = nautisk tussmørke: horisonten er såvidt skillbar. BMNT/EENT.
 *  −18°    = astronomisk tussmørke: ingen målbar soloppvarming av himmelen;
 *            «full nattmørke» begynner.
 */
export const SUN_THRESHOLDS = {
    sunrise: -0.833,
    civil: -6,
    nautical: -12,
    astronomical: -18,
};
/** Lysfase ut fra geometrisk solhøyde (grader). */
export function lightPhaseForElevation(elevationDeg) {
    if (elevationDeg >= SUN_THRESHOLDS.sunrise)
        return 'dag';
    if (elevationDeg >= SUN_THRESHOLDS.civil)
        return 'borgerlig-tussmørke';
    if (elevationDeg >= SUN_THRESHOLDS.nautical)
        return 'nautisk-tussmørke';
    if (elevationDeg >= SUN_THRESHOLDS.astronomical)
        return 'astronomisk-tussmørke';
    return 'natt';
}
/** Lysfase for et tidspunkt og en posisjon. */
export function lightPhaseAt(timeMs, lat, lon) {
    return lightPhaseForElevation(sunElevationDeg(timeMs, lat, lon));
}
function sampleAltitude(alt, fromMs, toMs, stepMs) {
    const tMs = [];
    const altDeg = [];
    for (let t = fromMs; t < toMs; t += stepMs) {
        tMs.push(t);
        altDeg.push(alt(t));
    }
    // Sørg for at endepunktet alltid er med, uansett om stepMs deler intervallet.
    if (tMs[tMs.length - 1] !== toMs) {
        tMs.push(toMs);
        altDeg.push(alt(toMs));
    }
    return { tMs, altDeg };
}
/** Bisekssjon på (alt − terskel) i et bracket med kjent fortegnsskifte. */
function bisect(alt, thresholdDeg, aMs, bMs) {
    let lo = aMs;
    let hi = bMs;
    const loNeg = alt(lo) - thresholdDeg < 0;
    for (let i = 0; i < 40 && hi - lo > 250; i++) {
        const mid = (lo + hi) / 2;
        if (alt(mid) - thresholdDeg < 0 === loNeg)
            lo = mid;
        else
            hi = mid;
    }
    return Math.round((lo + hi) / 2);
}
/** Alle kryssinger av `thresholdDeg` i de gitte samplene, finpusset med bisekssjon. */
function crossingsFromSamples(s, alt, thresholdDeg) {
    const out = [];
    for (let i = 1; i < s.tMs.length; i++) {
        const a = s.altDeg[i - 1] - thresholdDeg;
        const b = s.altDeg[i] - thresholdDeg;
        if (a === 0)
            continue; // håndteres av forrige/neste intervall
        if (a < 0 === b < 0)
            continue;
        out.push({ atMs: bisect(alt, thresholdDeg, s.tMs[i - 1], s.tMs[i]), rising: a < 0 });
    }
    return out;
}
/**
 * Generisk terskel-søk: alle kryssinger av `thresholdDeg` for en vilkårlig
 * høydefunksjon i [fromMs, toMs]. Gjenbrukes av `moon.ts` for måne opp/ned.
 * `stepMs` er grovskannet; kryssinger som er kortere enn ett steg kan i teorien
 * gå tapt (praktisk risiko bare nær polarsirkelen med svært små steg-vinkler).
 */
export function findThresholdCrossings(alt, thresholdDeg, fromMs, toMs, stepMs = 120_000) {
    return crossingsFromSamples(sampleAltitude(alt, fromMs, toMs, stepMs), alt, thresholdDeg);
}
/**
 * Solas meridianpassasje (transitt) nærmest lokal middag i UTC-døgnet til
 * `dayStartMs`. Iterativ korreksjon på timevinkelen (Meeus kap. 15-metoden).
 */
export function solarNoonMs(dayStartMs, lat, lon) {
    let t = dayStartMs + (12 - lon / 15) * 3_600_000;
    for (let i = 0; i < 4; i++) {
        const H = sunPosition(t, lat, lon).hourAngleDeg;
        t -= (H / 360) * DAY_MS;
    }
    return Math.round(t);
}
function startOfUtcDay(timeMs) {
    const d = new Date(timeMs);
    return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}
function isoDateUtc(timeMs) {
    return new Date(timeMs).toISOString().slice(0, 10);
}
function eventPair(s, alt, thresholdDeg, transitMs, maxElevationDeg) {
    const cs = crossingsFromSamples(s, alt, thresholdDeg);
    const rise = cs.find((c) => c.rising && c.atMs <= transitMs) ?? cs.find((c) => c.rising);
    const setC = [...cs].reverse().find((c) => !c.rising && c.atMs >= transitMs) ??
        [...cs].reverse().find((c) => !c.rising);
    if (rise || setC) {
        return {
            thresholdDeg,
            riseMs: rise ? rise.atMs : null,
            setMs: setC ? setC.atMs : null,
            state: 'inntreffer',
        };
    }
    return {
        thresholdDeg,
        riseMs: null,
        setMs: null,
        state: maxElevationDeg >= thresholdDeg ? 'alltid-over' : 'alltid-under',
    };
}
/**
 * Alle militært relevante soltider for UTC-døgnet som `timeMs` faller i.
 *
 * Vinduet er soldøgnet [solmiddag − 12 t, solmiddag + 12 t], ikke kalenderdøgnet.
 * Det garanterer kronologien
 *   astronomisk < nautisk (BMNT) < borgerlig < soloppgang < solmiddag
 *   < solnedgang < borgerlig < nautisk (EENT) < astronomisk
 * og gjør at hver terskel har høyst én opp- og én ned-kryssing.
 */
export function sunDay(timeMs, lat, lon) {
    const dayStart = startOfUtcDay(timeMs);
    const noonMs = solarNoonMs(dayStart, lat, lon);
    const midnightMs = noonMs - DAY_MS / 2;
    const alt = (ms) => sunElevationDeg(ms, lat, lon);
    // 2-minutters grovskann: fanger opp selv korte terskel-overskridelser nær
    // polarsirkelen, der sola bare så vidt tipper over en terskel.
    const s = sampleAltitude(alt, midnightMs, noonMs + DAY_MS / 2, 120_000);
    const maxElevationDeg = alt(noonMs);
    const minElevationDeg = alt(midnightMs);
    const sun = eventPair(s, alt, SUN_THRESHOLDS.sunrise, noonMs, maxElevationDeg);
    const civil = eventPair(s, alt, SUN_THRESHOLDS.civil, noonMs, maxElevationDeg);
    const nautical = eventPair(s, alt, SUN_THRESHOLDS.nautical, noonMs, maxElevationDeg);
    const astronomical = eventPair(s, alt, SUN_THRESHOLDS.astronomical, noonMs, maxElevationDeg);
    let state = 'normal';
    if (sun.state === 'alltid-over')
        state = 'midnattssol';
    else if (sun.state === 'alltid-under')
        state = 'mørketid';
    let dayLengthHours;
    if (sun.riseMs !== null && sun.setMs !== null) {
        dayLengthHours = (sun.setMs - sun.riseMs) / 3_600_000;
    }
    else {
        dayLengthHours = state === 'midnattssol' ? 24 : 0;
    }
    return {
        dateUtc: isoDateUtc(dayStart),
        solarNoonMs: noonMs,
        solarMidnightMs: midnightMs,
        maxElevationDeg,
        minElevationDeg,
        state,
        sunriseMs: sun.riseMs,
        sunsetMs: sun.setMs,
        dawnCivilMs: civil.riseMs,
        duskCivilMs: civil.setMs,
        bmntMs: nautical.riseMs,
        eentMs: nautical.setMs,
        dawnAstronomicalMs: astronomical.riseMs,
        duskAstronomicalMs: astronomical.setMs,
        dayLengthHours,
        events: { sun, civil, nautical, astronomical },
    };
}
/**
 * Neste lysfase-overgang etter `timeMs`. Returnerer null når ingen terskel
 * krysses innen `searchHours` — det er den riktige svaret ved midnattssol
 * (alltid dag) og midt i mørketida (alltid natt/tussmørke uten overgang).
 * Default 48 t dekker at et døgn kan mangle enkelte overganger nær
 * polarsirkelen.
 */
export function nextLightPhaseChange(timeMs, lat, lon, searchHours = 48) {
    const alt = (ms) => sunElevationDeg(ms, lat, lon);
    const toMs = timeMs + searchHours * 3_600_000;
    const s = sampleAltitude(alt, timeMs, toMs, 120_000);
    let best = null;
    for (const thresholdDeg of Object.values(SUN_THRESHOLDS)) {
        for (const c of crossingsFromSamples(s, alt, thresholdDeg)) {
            if (c.atMs <= timeMs)
                continue;
            if (!best || c.atMs < best.c.atMs)
                best = { c, thresholdDeg };
        }
    }
    if (!best)
        return null;
    // Fasen rett før/etter kryssingen (±2 s for å komme klar av terskelen).
    const from = lightPhaseForElevation(alt(best.c.atMs - 2000));
    const to = lightPhaseForElevation(alt(best.c.atMs + 2000));
    return {
        atMs: best.c.atMs,
        secondsUntil: (best.c.atMs - timeMs) / 1000,
        from,
        to,
        thresholdDeg: best.thresholdDeg,
        rising: best.c.rising,
    };
}
//# sourceMappingURL=sun.js.map